import { Box, Radio, Stack, Typography } from '@mui/material';
import { ErrorMessage, useFormikContext } from 'formik';
import { DepositBehavior } from '@prisma/client';
import TextInput from '../../form/base/TextInput';
import { FlashDesignFormPayload } from '../../../types/flashbooks';
import { FLASH_DESIGN_DEPOSIT_OPTIONS } from '../../../constants/flashbooks';

const renderError = (msg: string) => (
  <Typography
    color="error.dark"
    variant="caption"
    sx={{ mt: 0.5, display: 'block' }}>
    {msg}
  </Typography>
);

export default function PaymentRulesSelector() {
  const { values, setFieldValue } = useFormikContext<FlashDesignFormPayload>();

  const onSelect = (next: DepositBehavior) => {
    setFieldValue('depositBehavior', next);
    if (next !== 'FLAT_FEE') setFieldValue('depositAmount', '');
    if (next !== 'PERCENTAGE') setFieldValue('depositPercentage', '');
  };

  return (
    <Box width="100%">
      <Stack gap={1}>
        {FLASH_DESIGN_DEPOSIT_OPTIONS.map(option => {
          const selected = values.depositBehavior === option.value;
          return (
            <Box
              key={option.value}
              onClick={() => onSelect(option.value)}
              role="radio"
              aria-checked={selected}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(option.value);
                }
              }}
              sx={{
                cursor: 'pointer',
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'darkerLightGrey.main',
                bgcolor: selected
                  ? 'rgba(255, 48, 8, 0.02)'
                  : 'background.paper',
                py: 1.75,
                px: 2,
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': {
                  borderColor: selected ? 'primary.main' : 'medGrey.main',
                },
              }}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Radio
                  checked={selected}
                  size="small"
                  onChange={() => onSelect(option.value)}
                  sx={{ p: 0 }}
                />
                <Stack flex={1}>
                  <Typography variant="h5" color="common.black">
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" mt="2px">
                    {option.description}
                  </Typography>
                </Stack>
              </Stack>
              {selected && option.value === 'FLAT_FEE' ? (
                <Box mt={2} ml={4}>
                  <TextInput
                    name="depositAmount"
                    label="Deposit amount"
                    placeholder="50"
                  />
                  <ErrorMessage name="depositAmount" render={renderError} />
                </Box>
              ) : null}
              {selected && option.value === 'PERCENTAGE' ? (
                <Box mt={2} ml={4}>
                  <TextInput
                    name="depositPercentage"
                    label="Deposit percentage"
                    placeholder="25"
                    suffix="%"
                  />
                  <ErrorMessage name="depositPercentage" render={renderError} />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
